import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Key, Copy, Trash2, Plus, CheckCircle } from 'lucide-react';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Spinner from '../components/ui/Spinner';
import { useApiKeys, useCreateApiKey, useRevokeApiKey } from '../hooks/useApiKeys';
import type { ApiKey } from '../services/data';

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString([], {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

interface GenerateModalProps {
  onClose: () => void;
}

function GenerateModal({ onClose }: GenerateModalProps) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const createMutation = useCreateApiKey();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    const result = await createMutation.mutateAsync(name.trim());
    setCreatedKey(result.key);
  };

  const handleCopy = () => {
    if (!createdKey) return;
    navigator.clipboard.writeText(createdKey).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          {t('apiKeys.generate')}
        </h2>

        {!createdKey ? (
          <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('apiKeys.name')}
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={t('apiKeys.namePlaceholder')}
                required
                maxLength={100}
                autoComplete="off"
              />
            </div>
            {createMutation.isError && (
              <p className="text-sm text-red-600">{t('common.error')}</p>
            )}
            <div className="flex justify-end gap-3">
              <Button variant="secondary" type="button" onClick={onClose} size="sm">
                {t('common.cancel')}
              </Button>
              <Button
                variant="primary"
                type="submit"
                size="sm"
                disabled={createMutation.isPending || !name.trim()}
              >
                {createMutation.isPending ? <Spinner size="sm" /> : t('apiKeys.generate')}
              </Button>
            </div>
          </form>
        ) : (
          <div className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
              <p className="text-sm text-amber-800 font-medium">
                {t('apiKeys.copyWarning')}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('apiKeys.keyValue')}
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  readOnly
                  value={createdKey}
                  className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm font-mono bg-gray-50 focus:outline-none"
                />
                <Button
                  variant={copied ? 'secondary' : 'primary'}
                  size="sm"
                  type="button"
                  onClick={handleCopy}
                  className="shrink-0"
                >
                  {copied ? (
                    <CheckCircle className="w-4 h-4" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>
            <div className="flex justify-end">
              <Button variant="primary" size="sm" onClick={onClose}>
                {t('common.close')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

interface RevokeDialogProps {
  apiKey: ApiKey;
  onClose: () => void;
}

function RevokeDialog({ apiKey, onClose }: RevokeDialogProps) {
  const { t } = useTranslation();
  const revokeMutation = useRevokeApiKey();

  const handleRevoke = async () => {
    await revokeMutation.mutateAsync(apiKey.id);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-sm p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">
          {t('apiKeys.confirmRevoke')}
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          {t('apiKeys.confirmRevokeDesc', { name: apiKey.name })}
        </p>
        {revokeMutation.isError && (
          <p className="text-sm text-red-600 mb-3">{t('common.error')}</p>
        )}
        <div className="flex justify-end gap-3">
          <Button variant="secondary" size="sm" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={handleRevoke}
            disabled={revokeMutation.isPending}
          >
            {revokeMutation.isPending ? <Spinner size="sm" /> : t('apiKeys.revoke')}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function ApiKeys() {
  const { t } = useTranslation();
  const { data: apiKeys, isLoading, isError } = useApiKeys();
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<ApiKey | null>(null);
  const [showRevoked, setShowRevoked] = useState(false);

  const activeKeys = apiKeys?.filter((k) => k.is_active) ?? [];
  const revokedKeys = apiKeys?.filter((k) => !k.is_active) ?? [];
  const visibleKeys = showRevoked ? (apiKeys ?? []) : activeKeys;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Key className="w-6 h-6 text-gray-500" />
          <h1 className="text-3xl font-bold text-gray-900">{t('apiKeys.title')}</h1>
        </div>
        <Button
          variant="primary"
          size="sm"
          onClick={() => setShowGenerateModal(true)}
          className="flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          {t('apiKeys.generate')}
        </Button>
      </div>

      <Card>
        {isLoading ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : isError ? (
          <p className="text-center text-red-600 py-8">{t('common.error')}</p>
        ) : activeKeys.length === 0 && !showRevoked ? (
          <div className="text-center py-12">
            <Key className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">{t('apiKeys.empty')}</p>
            {revokedKeys.length > 0 && (
              <button
                className="mt-3 text-sm text-gray-400 underline hover:text-gray-600"
                onClick={() => setShowRevoked(true)}
              >
                {t('apiKeys.showRevoked', { count: revokedKeys.length })}
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-medium text-gray-700">
                      {t('apiKeys.name')}
                    </th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">
                      {t('apiKeys.prefix')}
                    </th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">
                      {t('apiKeys.lastUsed')}
                    </th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">
                      {t('apiKeys.status')}
                    </th>
                    <th className="py-3 px-4" />
                  </tr>
                </thead>
                <tbody>
                  {visibleKeys.map((key) => (
                    <tr
                      key={key.id}
                      className={`border-b border-gray-100 ${key.is_active ? 'hover:bg-gray-50' : 'opacity-50'}`}
                    >
                      <td className="py-3 px-4 font-medium text-gray-900">{key.name}</td>
                      <td className="py-3 px-4 font-mono text-gray-600">{key.key_prefix}…</td>
                      <td className="py-3 px-4 text-gray-600">{formatDate(key.last_used_at)}</td>
                      <td className="py-3 px-4">
                        {key.is_active ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            {t('apiKeys.active')}
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                            {t('apiKeys.revoked')}
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-right">
                        {key.is_active && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setRevokeTarget(key)}
                            className="text-red-600 hover:bg-red-50"
                            title={t('apiKeys.revoke')}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {revokedKeys.length > 0 && (
              <div className="px-4 py-3 border-t border-gray-100">
                <button
                  className="text-sm text-gray-400 underline hover:text-gray-600"
                  onClick={() => setShowRevoked((v) => !v)}
                >
                  {showRevoked
                    ? t('apiKeys.hideRevoked')
                    : t('apiKeys.showRevoked', { count: revokedKeys.length })}
                </button>
              </div>
            )}
          </>
        )}
      </Card>

      {showGenerateModal && (
        <GenerateModal onClose={() => setShowGenerateModal(false)} />
      )}
      {revokeTarget && (
        <RevokeDialog
          apiKey={revokeTarget}
          onClose={() => setRevokeTarget(null)}
        />
      )}
    </div>
  );
}
